import { useState, type FormEvent, type MouseEvent } from "react";
import { heroStats, hotKeywords, timelineNodes } from "@/mocks/home";

export default function Hero() {
  const [keyword, setKeyword] = useState("");

  const handleAnchor = (event: MouseEvent<HTMLAnchorElement>, hash: string) => {
    event.preventDefault();
    const target = document.querySelector(hash);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = document.querySelector("#events");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="top" className="relative flex min-h-[620px] w-full flex-col overflow-hidden md:min-h-[760px]">
      <img
        src="https://readdy.ai/api/search-image?query=Dramatic%20abstract%20editorial%20background%20in%20deep%20crimson%20and%20warm%20amber%2C%20sweeping%20light%20beams%20through%20dark%20stage%20haze%2C%20subtle%20film%20grain%20and%20paper%20texture%2C%20spotlight%20rays%20converging%20at%20center%2C%20cinematic%20magazine%20cover%20mood%2C%20generous%20dark%20space%20at%20the%20top%20for%20a%20headline&width=1800&height=1000&seq=lh-hero-001&orientation=landscape"
        alt="灵灵黑料事件汇总主视觉"
        title="灵灵黑料 - 事件汇总全盘点与时间线大合集"
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/65"></div>

      <div className="relative z-10 flex w-full flex-1 items-center justify-center px-4 pb-14 pt-28 md:px-10 md:pt-36">
        <div className="w-full max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-3.5 py-1.5 font-label text-xs text-background-50 backdrop-blur md:text-sm">
            <i className="ri-bookmark-3-line text-accent-400"></i>
            从发酵到引爆 · 一次看懂全过程
          </span>

          <h1 className="mt-6 font-heading text-[26px] font-bold leading-snug text-background-50 md:text-5xl md:leading-tight lg:text-6xl">
            灵灵黑料事件汇总全盘点
            <span className="mt-2 block text-[22px] text-accent-400 md:mt-3 md:text-4xl lg:text-5xl">
              灵灵黑料时间线大合集
            </span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-background-50/85 md:mt-6 md:text-lg">
            按时间线梳理从发酵到引爆的完整经过，把关键节点、外围动作与版本差异一次摆清。想快速搞清全貌，从这里开始就不会迷路。
          </p>

          <form
            onSubmit={handleSearch}
            className="mx-auto mt-7 flex w-full max-w-xl items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 p-1.5 backdrop-blur md:mt-8"
          >
            <span className="ml-2 flex h-9 w-9 shrink-0 items-center justify-center text-background-50/80">
              <i className="ri-search-2-line text-lg"></i>
            </span>
            <input
              type="text"
              name="site_search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索节点、时间或关键词"
              aria-label="搜索事件节点"
              className="h-10 min-w-0 flex-1 bg-transparent font-label text-sm text-background-50 outline-none placeholder:text-background-50/55 md:text-base"
            />
            <button
              type="submit"
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-background-50 px-5 py-2.5 font-label text-sm font-medium text-foreground-950 transition-colors hover:bg-background-100"
            >
              搜索节点
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="font-label text-xs text-background-50/60">热词：</span>
            {hotKeywords.map((word) => (
              <a
                key={word}
                href="#events"
                onClick={(event) => handleAnchor(event, "#events")}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-50/25 bg-background-50/5 px-3 py-1.5 font-label text-xs text-background-50/90 transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                {word}
              </a>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="#timeline"
              onClick={(event) => handleAnchor(event, "#timeline")}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              时间线速览
            </a>
            <a
              href="#events"
              onClick={(event) => handleAnchor(event, "#events")}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-50/45 px-5 py-3 font-label text-sm text-background-50 transition-colors hover:bg-background-50/10"
            >
              全盘点合集
            </a>
            <a
              href="#gallery"
              onClick={(event) => handleAnchor(event, "#gallery")}
              className="flex cursor-pointer flex-nowrap items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 font-label text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-download-2-line"></i>
              图文打包下载
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-y-6 md:mt-12 md:grid-cols-4">
            {heroStats.map((stat, index) => (
              <div
                key={stat.label}
                className={`px-2 text-center md:px-4 ${index !== 0 ? "md:border-l md:border-background-50/20" : ""}`}
              >
                <div className="font-heading text-2xl font-bold text-background-50 md:text-3xl">{stat.value}</div>
                <div className="mt-1 font-label text-xs text-background-50/70 md:text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-auto w-full overflow-hidden border-t border-background-50/15 bg-black/30 py-3 backdrop-blur-sm">
        <div className="flex w-max animate-marquee items-center gap-8 whitespace-nowrap px-4">
          {[...timelineNodes, ...timelineNodes].map((node, index) => (
            <span key={`${node.id}-${index}`} className="flex items-center gap-2 font-label text-xs text-background-50/75 md:text-sm">
              <i className="ri-time-line text-accent-400"></i>
              <span className="font-medium text-background-50">{node.date}</span>
              <span>{node.title}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}